import {
  Incidents,
  InvestigationAffectedBodyPartsEnum,
  InvestigationClassifyIllnessEnum,
  InvestigationReportingControllerFindAllInjuriesIncidentClassificationEnum,
  InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum,
  InvestigationTypeEnum,
  InvestigationQuestionWithCategory,
  Attachments,
} from '@EHSNavigator/backend';
import CancelIcon from '../../assets/icons/sidebar-cross-icon.svg?react';
import clsx from 'clsx';
import React from 'react';
import Button from '../../components/Button';
import ChevronIcon from '../../assets/icons/chevron-down-icon.svg?react';
import DownloadIcon from '../../assets/icons/upload-cloud-line.svg?react';

import HelpActiveIcon from '../../assets/icons/help-active-icon.svg?react';
import ChevronLeftIcon from '../../assets/icons/chevron-down-icon.svg?react';
import { toggleSwitch } from '../../reducers/switch';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router';
import InjuryIcon from '../../assets/icons/injuries-icon.svg?react';
import moment from 'moment';
import RadioButtons from '../../components/RadioButtons';
import { useButtonSizeSmall } from '../../utils/buttonSizes';
import Select from '../../components/Select';
import FileInput from '../../components/FileInput';
import Input from '../../components/Input';
import DatePicker from '../../components/DatePicker';

interface InvestigationReportingStepOneProps {
  otherClasses?: string;
  formHandler: any;
  handleNextStep: (steps: number) => void;
  data: Incidents | undefined;
  questions: InvestigationQuestionWithCategory[];
  attachments: Attachments[];
  handleChangeAttachments: (e: Attachments[]) => void;
}

const InvestigationReportingStepOne: React.FC<
  InvestigationReportingStepOneProps
> = ({
  otherClasses,
  formHandler,
  handleNextStep,
  data,
  questions,
  attachments,
  handleChangeAttachments,
}) => {
  const investigationReportingStepOneClasses = clsx(otherClasses);
  const dispatch = useDispatch();
  const sm = useButtonSizeSmall();
  const dateOfHire = data?.user?.dateOfHire;
  const momentDate = moment(dateOfHire).isValid()
    ? moment(dateOfHire).toDate()
    : moment('2010-01-01').toDate();
  const handleRemoveFile = (fileToRemove: any) => {
    const updatedFiles =
      attachments.filter((file) => file.name !== fileToRemove.name) || [];
    handleChangeAttachments(updatedFiles);
  };

  return (
    <div
      className={investigationReportingStepOneClasses}
      data-testid="investigation-reporting-step-one"
    >
      <div className="flex flex-row items-start gap-2.5 md:hidden">
        <button
          type="button"
          className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 w-fit cursor-pointer border p-0.5"
          onClick={() => dispatch(toggleSwitch())}
        >
          <ToggleIcon />
        </button>
        <div className="flex flex-col gap-1">
          <span className="text-brand-byzantine font-inter-variable text-xs leading-4 font-bold">
            INJURY/ILLNESS
          </span>
          <h4 className="text-neutral-tones-text font-inter-variable font-bold">
            Investigate Injury/Illness
          </h4>
          <p className="text-brand-byzantine font-inter-variable text-base leading-5 font-bold">
            STEP 1 /{' '}
            {formHandler?.values?.files?.length > 0 || attachments?.length > 0
              ? 4
              : questions?.length > 0
                ? 5
                : 4}
          </p>
        </div>
      </div>
      <div className="hidden md:block">
        <div className="flex justify-between">
          <Button
            href="/injuries"
            variant="link"
            otherClasses="gap-2.5 w-fit mb-15"
          >
            <span className="bg-brand-byzantine rounded-5 flex h-8 w-8 items-center justify-center">
              <ChevronLeftIcon className="h-7 w-7 rotate-90 [&>path]:fill-white" />
            </span>
            <strong>Return</strong>
          </Button>

          <div className="border-neutral-tones-text/10 bg-card-backgrounds/5 hidden items-center gap-2.5 rounded-full border p-2.5 md:flex">
            <HelpActiveIcon className="[&>path]:fill-brand-papery h-5 w-5" />
            <p className="text-neutral-tones-text font-inter-variable text-sm leading-4 font-medium italic">
              You can add custom questions to your Organization.
            </p>
            <Button
              variant="link"
              href="/custom-questions"
              otherClasses="!text-brand-byzantine !font-inter-variable !text-sm !leading-4 !font-bold"
            >
              Add Questions
            </Button>
          </div>
        </div>
        <h4 className="text-neutral-tones-text font-inter-variable mt-2.5 font-bold">
          Investigate Injury/Illness
        </h4>
        <p className="text-brand-byzantine font-inter-variable mt-15 text-xl leading-6 font-bold">
          STEP 1 /{' '}
          {formHandler?.values?.files?.length > 0 || attachments?.length > 0
            ? 4
            : questions?.length > 0
              ? 5
              : 4}
        </p>
      </div>
      <div className="border-neutral-tones-text/10 bg-card-backgrounds/5 mt-2.5 flex items-center gap-2.5 rounded-full border p-2.5 md:hidden">
        <HelpActiveIcon className="[&>path]:fill-brand-papery h-4 min-h-4 w-4 min-w-4 md:h-5 md:min-h-5 md:w-5 md:min-w-5" />
        <p className="text-neutral-tones-text font-inter-variable text-xs leading-3 font-medium italic">
          You can add custom questions to your Organization.
        </p>
        <Button
          variant="link"
          href="/custom-questions"
          otherClasses="!text-brand-byzantine !font-inter-variable !text-xs !leading-3 !font-bold whitespace-nowrap"
        >
          Add Questions
        </Button>
      </div>

      <div className="border-y-neutral-tones-text/10 my-8 w-full border-y py-5 lg:my-9">
        <div className="bg-card-backgrounds/5 rounded-10 border-neutral-tones-text/10 flex w-full items-center gap-2.5 border-2 p-15">
          <InjuryIcon className="[&>path]:fill-brand-fair-orange h-12 w-12 md:h-9 md:w-9 lg:h-12 lg:w-12" />
          <div className="flex flex-1 flex-col gap-1 lg:gap-2.5">
            <span className="font-inter-variable text-brand-fair-orange -tracking-form-input lg:-tracking-button text-base leading-5 font-medium lg:text-xl lg:leading-6 lg:font-bold">
              Linked Injury/Illness
            </span>
            <div className="flex items-center gap-15">
              <p className="text-neutral-tones-text -tracking-form-input lg:-tracking-text-area text-base leading-5 font-bold capitalize lg:text-lg lg:leading-5">
                {data?.title}
              </p>
              <span className="border-l-neutral-tones-text/10 hidden h-5 border-l md:block"></span>
              <p className="font-inter-variable text-neutral-tones-text/80 -tracking-form-label lg:-tracking-form-input hidden text-sm leading-5 font-normal whitespace-nowrap md:block lg:text-base">
                Reference ID{' '}
                <span className="text-neutral-tones-text/50 uppercase">
                  {data?.incidentRefId}
                </span>
              </p>
            </div>
          </div>
          <Link target="_blank" to={`/incident-review/${data?.id}`}>
            <ChevronLeftIcon className="[&>path]:fill-neutral-tones-text h-6 w-6 -rotate-90 md:h-12 md:w-12" />
          </Link>
        </div>
      </div>

      <div>
        <div className="grid grid-cols-1 items-start gap-9 md:grid-cols-2">
          <div className="flex flex-col">
            <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-base leading-5 font-bold lg:mb-25 lg:text-xl lg:leading-6">
              Reported Date & Time
            </h6>
            {data?.dateUndetermined ? (
              <div className="flex items-start gap-75 md:gap-12 lg:gap-75">
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-sm leading-6 font-bold lg:text-xl lg:leading-6">
                    Date & Time
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-sm leading-4 font-normal lg:text-base lg:leading-5">
                    Date & Time Undetermined
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-75 md:gap-12 lg:gap-75">
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-sm leading-6 font-bold lg:text-xl lg:leading-6">
                    Date
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-sm leading-4 font-normal lg:text-base lg:leading-5">
                    {moment(data?.incidentDate).format('MMMM D, YYYY')}
                  </p>
                </div>
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-sm leading-6 font-bold lg:text-xl lg:leading-6">
                    Time
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-sm leading-4 font-normal lg:text-base lg:leading-5">
                    {moment(data?.incidentTime, 'HH:mm:ss').format('h:mm A')}
                  </p>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col">
            <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-base leading-5 font-bold lg:mb-25 lg:text-xl lg:leading-6">
              Location of Incident
            </h6>
            <div className="flex items-start gap-75">
              <div className="flex flex-col gap-2.5">
                <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-sm leading-6 font-bold lg:text-xl lg:leading-6">
                  Location Address
                </h6>
                <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-sm leading-4 font-normal lg:text-base lg:leading-5">
                  {data?.location}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="border-t-neutral-tones-text/10 mt-15 grid grid-cols-1 items-start gap-9 border-t pt-15 md:grid-cols-2">
          <div className="flex flex-col">
            <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-base leading-5 font-bold lg:mb-25 lg:text-xl lg:leading-6">
              Injury or Illness<span className="text-brand-posh-pink">*</span>
            </h6>
            <RadioButtons
              options={Object.entries(InvestigationTypeEnum).map(
                ([, value]) => ({
                  label: value,
                  value: value,
                }),
              )}
              onBlur={formHandler?.handleBlur}
              value={formHandler.values.injuryIllness}
              onChange={(e: string) => {
                formHandler.setFieldTouched('injuryIllness', false);
                formHandler.setFieldValue('injuryIllness', e);
                formHandler.setFieldValue('classifyIllness', '');
                formHandler.setFieldTouched('injuryIllness', '');
              }}
              otherClasses="flex-row md:flex-col"
              errorMessage={
                formHandler?.touched.injuryIllness &&
                formHandler?.errors.injuryIllness
                  ? formHandler?.errors.injuryIllness
                  : ''
              }
            />
          </div>
          <div className="flex flex-col">
            <h6 className="font-inter-variable text-brand-byzantine -tracking-button text-base leading-5 font-bold lg:text-xl lg:leading-6">
              Body Parts Affected
              <span className="text-brand-posh-pink">*</span>
            </h6>
            <p className="font-inter-variable text-neutral-tones-text mt-1 text-base leading-5 font-normal">
              Select the body parts of the employee affected by the
              injury/illness due to the incident:
            </p>

            <div className="relative mt-5 grid max-w-380 grid-cols-2 gap-2.5 sm:grid-cols-3">
              {Object?.values(InvestigationAffectedBodyPartsEnum)?.map(
                (node, index) => (
                  <Button
                    type="button"
                    variant="secondary"
                    size="smaller"
                    key={index}
                    otherClasses={clsx(
                      '',
                      formHandler.values.bodyPartAffected?.includes(node)
                        ? '!border-transparent !bg-brand-byzantine !text-white'
                        : '',
                    )}
                    onClick={() => {
                      formHandler.setFieldValue(
                        'bodyPartAffected',
                        formHandler.values.bodyPartAffected?.includes(node)
                          ? formHandler.values.bodyPartAffected?.filter(
                              (item: string) => item !== node,
                            )
                          : [...formHandler.values.bodyPartAffected, node],
                      );
                    }}
                  >
                    {node}
                  </Button>
                ),
              )}
              {formHandler?.touched.bodyPartAffected &&
                formHandler?.errors.bodyPartAffected && (
                  <span className="font-inter-variable text-brand-posh-pink absolute -bottom-5 left-0 text-xs font-normal">
                    {formHandler?.errors.bodyPartAffected}
                  </span>
                )}
            </div>
          </div>
        </div>
        <div className="border-b-neutral-tones-text/10 mt-15 grid grid-cols-1 items-start gap-9 border-b pt-15 pb-6 md:grid-cols-2">
          <Select
            label={
              <h6 className="font-inter-variable text-brand-byzantine -tracking-button text-base leading-5 font-bold lg:text-xl lg:leading-6">
                Type of Loss<span className="text-brand-posh-pink">*</span>
              </h6>
            }
            list={Object.entries(
              InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum,
            ).map(([, value]) => ({
              label: value,
              value: value,
            }))}
            value={formHandler.values.typeOfLoss}
            disabled={formHandler.isSubmitting}
            placeholder="Select Type of Loss"
            otherClasses={{
              container: 'w-full gap-5 lg:gap-25',
              listContainer: 'top-auto bottom-69',
            }}
            onSelect={(e) => {
              formHandler?.setFieldValue('typeOfLoss', e, true);
              formHandler?.setFieldTouched('typeOfLoss', false);
              formHandler?.setFieldValue('daysAwayFromWork', '');
              formHandler?.setFieldValue('jobRestrictionDays', '');
              formHandler?.setFieldValue('dateOfDeath', '');
              formHandler?.setFieldTouched('dateOfDeath', false);
              formHandler?.setFieldValue('treatmentFacilityType', '');
            }}
            errorMessage={
              formHandler?.touched.typeOfLoss && formHandler?.errors.typeOfLoss
                ? formHandler?.errors.typeOfLoss
                : ''
            }
          />
          <Select
            label={
              <h6 className="font-inter-variable text-brand-byzantine -tracking-button text-base leading-5 font-bold lg:text-xl lg:leading-6">
                Classification<span className="text-brand-posh-pink">*</span>
              </h6>
            }
            list={Object.entries(
              InvestigationReportingControllerFindAllInjuriesIncidentClassificationEnum,
            ).map(([, value]) => ({
              label: value,
              value: value,
            }))}
            value={formHandler.values.classification}
            disabled={formHandler.isSubmitting}
            placeholder="Select Classification"
            otherClasses={{
              container: 'w-full gap-5 lg:gap-25',
              listContainer: 'top-auto bottom-69',
            }}
            onSelect={(e) => {
              formHandler?.setFieldValue('classification', e, true);
              formHandler?.setFieldTouched('classification', false);

              formHandler?.setFieldValue('treatmentFacilityType', '');
            }}
            errorMessage={
              formHandler?.touched.classification &&
              formHandler?.errors.classification
                ? formHandler?.errors.classification
                : ''
            }
          />
          {formHandler.values.injuryIllness ===
            InvestigationTypeEnum.Illness && (
            <Select
              label={
                <h6 className="font-inter-variable text-brand-byzantine -tracking-button text-base leading-5 font-bold lg:text-xl lg:leading-6">
                  Classify Illness
                  <span className="text-brand-posh-pink">*</span>
                </h6>
              }
              list={Object.entries(InvestigationClassifyIllnessEnum).map(
                ([, value]) => ({
                  label: value,
                  value: value,
                }),
              )}
              value={formHandler.values.classifyIllness}
              disabled={formHandler.isSubmitting}
              placeholder="Select Classify Illness"
              otherClasses={{
                container: 'w-full gap-5 lg:gap-25',
                listContainer: 'top-auto bottom-69',
              }}
              onSelect={(e) => {
                // formHandler?.setFieldTouched('classifyIllness', false);
                formHandler?.setFieldValue('classifyIllness', e, true);
              }}
              errorMessage={
                formHandler?.touched.classifyIllness &&
                formHandler?.errors.classifyIllness
                  ? formHandler?.errors.classifyIllness
                  : ''
              }
            />
          )}
          {formHandler.values.typeOfLoss ===
            InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum.DaysAwayFromWork && (
            <Input
              label={
                <h6 className="font-inter-variable text-brand-byzantine -tracking-button text-base leading-5 font-bold lg:text-xl lg:leading-6">
                  Days Away From Work
                  <span className="text-brand-posh-pink">*</span>
                </h6>
              }
              type="number"
              min={0}
              onKeyDown={(e: any) => {
                if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              name="daysAwayFromWork"
              placeholder="Enter Days Away From Work"
              value={formHandler.values.daysAwayFromWork}
              onChange={(e) => {
                formHandler.setFieldValue('daysAwayFromWork', e.target.value);
              }}
              otherClasses={{
                container: 'w-full gap-5 lg:gap-25',
              }}
              errorMessage={
                formHandler?.touched.daysAwayFromWork &&
                formHandler?.errors.daysAwayFromWork
                  ? formHandler?.errors.daysAwayFromWork
                  : ''
              }
              onBlur={() =>
                formHandler?.setFieldTouched('daysAwayFromWork', true)
              }
            />
          )}
          {formHandler.values.typeOfLoss ===
            InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum.RestrictedWorkActivityOrJobTransfer && (
            <Input
              label={
                <h6 className="font-inter-variable text-brand-byzantine -tracking-button text-base leading-5 font-bold lg:text-xl lg:leading-6">
                  Restricted Work Activity or Job Transfer
                  <span className="text-brand-posh-pink">*</span>
                </h6>
              }
              type="number"
              min={0}
              onKeyDown={(e: any) => {
                if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              name="jobRestrictionDays"
              placeholder="Enter Number of Restricted Day's"
              value={formHandler.values.jobRestrictionDays}
              onChange={(e) => {
                formHandler.setFieldValue('jobRestrictionDays', e.target.value);
              }}
              otherClasses={{
                container: 'w-full gap-5 lg:gap-25',
              }}
              errorMessage={
                formHandler?.touched.jobRestrictionDays &&
                formHandler?.errors.jobRestrictionDays
                  ? formHandler?.errors.jobRestrictionDays
                  : ''
              }
              onBlur={() =>
                formHandler?.setFieldTouched('jobRestrictionDays', true)
              }
            />
          )}
          {formHandler.values.typeOfLoss ===
            InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum.Death && (
            <DatePicker
              otherClasses={{
                container:
                  'w-full gap-5 lg:gap-25 lg:[&>button]:top-[73%] [&>button]:top-[70%]',
                dropdownMainContainer: 'top-auto bottom-69',
              }}
              label={
                <h6 className="font-inter-variable text-brand-byzantine -tracking-button text-base leading-5 font-bold lg:text-xl lg:leading-6">
                  Date Of Death
                  <span className="text-brand-posh-pink">*</span>
                </h6>
              }
              name="dateOfDeath"
              startMonth={momentDate}
              endMonth={new Date()}
              value={formHandler?.values?.dateOfDeath}
              placeholder="Select date of death"
              // onBlur={() => formHandler?.setFieldTouched('dateOfDeath', false)}
              onBlur={() => {
                if (formHandler?.values?.dateOfDeath) {
                  formHandler?.setFieldTouched('dateOfDeath', true);
                }
              }}
              disabled={formHandler.isSubmitting}
              onChange={(e) => {
                formHandler?.setFieldValue('dateOfDeath', e, true);
              }}
              errorMessage={
                formHandler?.touched.dateOfDeath &&
                formHandler?.errors.dateOfDeath
                  ? formHandler?.errors.dateOfDeath
                  : ''
              }
            />
          )}
        </div>

        {formHandler?.values?.files?.length === 0 && (
          <div className="mt-11 mb-9 flex flex-wrap items-center gap-5">
            <h6 className="text-neutral-tones-text font-inter-variable text-sm leading-4 lg:text-xl lg:leading-6">
              Would you like to upload your own investigation or use the
              Organizational investigation tool?
            </h6>
            <Button
              variant="link"
              type="button"
              otherClasses="!text-lg gap-2.5 !font-bold !font-inter-variable !text-brand-byzantine lg:flex hidden"
              onClick={() => {
                document
                  .getElementById('upload-investigation-file-input')
                  ?.click();
              }}
            >
              <DownloadIcon className="h-5 w-5 [&>path]:fill-current" />
              Upload Investigation
            </Button>
            <Button
              variant="primary"
              type="button"
              size="smaller"
              onClick={() => {
                document
                  .getElementById('upload-investigation-file-input')
                  ?.click();
              }}
              otherClasses="lg:hidden"
            >
              <DownloadIcon className="h-5 w-5 [&>path]:fill-current" />
              Upload Investigation
            </Button>
          </div>
        )}
        <div className={clsx('mt-11 mb-9')}>
          <FileInput
            label="Uploaded Investigation"
            name="files"
            id="upload-investigation-file-input"
            selectedFiles={formHandler?.values?.files}
            onChange={(e) => {
              formHandler?.setFieldValue('files', e, true);
            }}
            accept={{
              'application/pdf': ['.pdf'],
              'application/msword': ['.doc'],
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                ['.docx'],
            }}
            onBlur={formHandler?.handleBlur}
            errorMessage={
              formHandler?.errors?.files ? formHandler?.errors?.files : ''
            }
            otherClasses={{
              container:
                formHandler?.values?.files?.length > 0 ? 'block' : 'hidden',
            }}
          />
          {attachments?.length > 0 && (
            <div className="mt-4 flex flex-col gap-2">
              <p className="font-inter-variable text-neutral-tones-text/60 text-sm font-bold">
                Uploaded Files
              </p>
              {attachments?.map((att) => {
                return (
                  <div
                    key={att.id}
                    className={clsx(
                      'bg-additional-dropdown-select/10 ring-brand-byzantine flex w-full items-center justify-between rounded-md px-15 py-2 ring',
                    )}
                  >
                    <div className="flex items-center gap-2.5">
                      <p className="text-form-label text-neutral-tones-text -tracking-form-label leading-4 font-bold">
                        {att.name}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="cursor-pointer"
                      onClick={() => {
                        handleRemoveFile(att);
                      }}
                    >
                      <CancelIcon className="[&>g>path]:fill-brand-posh-pink h-5 w-5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Button
        size={sm}
        otherClasses="w-185"
        type="button"
        onClick={() => handleNextStep(2)}
      >
        Next
        <ChevronIcon className="h-6 w-6 -rotate-90 [&>path]:fill-current" />
      </Button>
    </div>
  );
};

export default InvestigationReportingStepOne;
